import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Receipt } from 'lucide-react';

const COLORS = ['#f59e0b', '#ec4899', '#8b5cf6', '#10b981'];

export function TaxBreakdownChart({ data }) {
  console.log('TaxBreakdownChart data:', data);
  
  // Process tax data from Table21
  const processTaxData = (inputData) => {
    if (!inputData) return [];

    // Case 1: If data is already in the expected format
    if (Array.isArray(inputData) && inputData.length > 0) {
      // Check if it's already formatted correctly
      if (inputData[0].name !== undefined && inputData[0].amount !== undefined) {
        return inputData;
      }

      // Case 2: If data is from Table21 (API response)
      if (inputData[0].GSTPercentage !== undefined) {
        // Filter out zero tax amounts
        const filteredData = inputData.filter(item => item.GstAmount > 0);
        
        // Calculate total tax amount for percentages
        const totalTax = filteredData.reduce((sum, item) => sum + (item.GstAmount || 0), 0);
        
        return filteredData.map(item => ({
          name: `${item.GSTPercentage}% GST`,
          percentage: totalTax > 0 ? ((item.GstAmount || 0) / totalTax) * 100 : 0,
          amount: item.GstAmount || 0,
          orders: item.OrderCount || 0,
          taxRate: item.GSTPercentage || 0
        }));
      }

      // Case 3: Generic mapping
      return inputData.map(item => ({
        name: item.name || `${item.taxPercentage || 0}% Tax`,
        percentage: item.percentage || 0,
        amount: item.amount || 0,
        orders: item.orders || 0
      }));
    }

    return [];
  };

  const processedData = processTaxData(data);
  
  // Calculate total tax for summary
  const totalTax = processedData.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalOrders = processedData.reduce((sum, item) => sum + (item.orders || 0), 0);

  // If no data after processing, show empty state
  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Tax Breakdown</h3>
            <p className="text-sm text-slate-500 mt-1">15% vs 8% tax distribution</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl shadow-lg">
            <Receipt className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="h-[250px] flex items-center justify-center">
          <div className="text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No tax breakdown data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Tax Breakdown</h3>
          <p className="text-sm text-slate-500 mt-1">GST distribution by rate</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl shadow-lg">
          <Receipt className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Tax Collected</p>
          <p className="text-lg font-bold text-orange-700">
            Rs. {totalTax.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Taxable Orders</p>
          <p className="text-lg font-bold text-purple-700">{totalOrders}</p>
        </div>
      </div>

      <div style={{ width: '100%', height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={processedData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
              outerRadius={90}
              fill="#8884d8"
              dataKey="amount"
              strokeWidth={4}
              stroke="#fff"
            >
              {processedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                padding: '8px 12px'
              }}
              formatter={(value, name, props) => {
                const item = props.payload;
                return [
                  <div key="tooltip">
                    <div className="font-semibold text-slate-900">
                      Rs. {item.amount.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {item.orders} orders • {item.percentage.toFixed(1)}% of total
                    </div>
                  </div>,
                  item.name
                ];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        {processedData.map((item, index) => (
          <div 
            key={index} 
            className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-4 h-4 rounded-full shadow-md"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              ></div>
              <span className="text-sm font-bold text-slate-900">{item.name}</span>
            </div>
            <p className="text-xl font-bold text-slate-900">
              Rs. {item.amount.toLocaleString(undefined, { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-500">{item.orders} orders</span>
              <span className="text-xs font-medium text-orange-600">
                {item.percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Tax Rate Breakdown */}
      {processedData.length > 1 && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>Average Tax Rate:</span>
            <span className="font-semibold text-slate-900">
              {(processedData.reduce((sum, item) => sum + (item.taxRate * item.amount), 0) / totalTax || 0).toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}